import { observable, action, computed, toJS, values } from 'mobx';
import { createTransformer } from 'mobx-utils';
import { formatDate } from 'utility.js'

const STORE_MAP = 'STORE_MAP';

class MapStore {

  configStore

  constructor(rawState = {}, configStore) {

    this.configStore = configStore

    if(rawState.warehouses) {
      this.setWarehouses(rawState.warehouses);
    }
    if(rawState.warehouseSaturations) {
      this.setWarehouseSaturations(rawState.warehouseSaturations);
    }
    if(rawState.binsDetails) {
      this.setBinsDetails(rawState.binsDetails);
    }
    if(rawState.currentWarehouseId) {
      this.setCurrentWarehouseId(rawState.currentWarehouseId);
    }
    if(rawState.detailedBinId) {
      this.setDetailedBinId(rawState.detailedBinId);
    }
    if(rawState.containerRect) {
      this.setContainerRect(rawState.containerRect);
    }
    if(rawState.itemid) {
      this.setItemid(rawState.itemid);
    }
    if(rawState.date) {
      this.setDate(rawState.date);
    }
    if(rawState.workshift) {
      this.setWorkShift(rawState.workshift);
    }
  }
  toJS() {
    return {
      warehouses: toJS(this.warehouses),
      warehouseSaturations: toJS(this.warehouseSaturations),
      detailedBinData: toJS(this.binsDetails),
      currentWarehouseId: toJS(this.currentWarehouseId),
      detailedBinId: toJS(this.detailedBinId),
      containerRect: toJS(this.containerRect),
      itemid: toJS(this.itemid),
      date: toJS(this.date),
      workshift: toJS(this.workshift)
    };
  }

  getCurrentWarehouseCorridor = createTransformer((corridorId) => this.currentWarehouse.Corridors.find(corridor => corridor.Id === corridorId));

  @observable vehicles = new Map();
  @action setVehicles(v) {
    this.vehicles = this.vehicles.replace(v);
  }
  @action setVehicle(id, v) {
    this.vehicles.set(id, v);
  }

  @computed get corridors() {
    return this.warehouses
      .reduce((acc, current) => acc.concat(current.Corridors), [])
      .reduce((acc, current) => Object.defineProperty(current, current.Id, current), {});
  }

  @computed get currentWarehouseCorridors() {
    return this.currentWarehouse ? this.currentWarehouse.Corridors.reduce((acc, current) => Object.defineProperty(acc, current.Id, current), {}) : {};
  };

  @computed get currentWarehouseVehicles() {
    return values(this.vehicles).filter(vehicle => {
      return this.currentWarehouseCorridors.hasOwnProperty(vehicle.Corridor);
    });
  }

  @observable warehouses;
  @action setWarehouses(v) {
    for (const warehouse of v) {
      for (const corridor of warehouse.Corridors) {
        if (!corridor.hasOwnProperty('Blocks')) continue;
        for (const block of corridor.Blocks) {
          if (!block.hasOwnProperty('Bins')) continue;
          for (const bin of block.Bins) {
            const bindb = this.configStore.bins.find(_bin => _bin.name === bin.Id)
            if(bindb)bin.LevelNum = bindb.levelnum;
          }
        }
      }
    }
    this.warehouses = v;
  }

  @observable warehouseSaturations = {};
  @action setWarehouseSaturations(v) {
    this.warehouseSaturations = v;
  }

  binsDetails = observable.map({});
  @action setBinsDetails(v) {
    this.binsDetails = observable.map(v);
  }
  @action setBinDetails(id, v) {
    this.binsDetails.set(id, v);
  }

  @observable currentWarehouseId = null;
  @action setCurrentWarehouseId(c) {
    this.currentWarehouseId = c;
  }

  @observable itemid;
  @action setItemid(value) {
    this.itemid = value;
  }

  @observable date;
  @action setDate(value) {
    this.date = value;
  }
  @computed get dateFormatted() {
    return formatDate(this.date);
  }

  @observable workshift;
  @action setWorkShift(value) {
    this.workshift = value;
  }

  @observable detailedBinId = null;
  @action setDetailedBinId(c) {
    this.detailedBinId = c;
  }

  @computed get detailedBin() {
    return this.currentWarehouseBins.hasOwnProperty(this.detailedBinId) ? this.currentWarehouseBins[this.detailedBinId] : null;
  }

  @computed get detailedBinView() {

    const { detailedBin, binsDetails, detailedBinId } = this

    if(!detailedBin) return null

    const levels = []
    if (detailedBin) {
      for (let i = 0; i < detailedBin.LevelNum; i++) {
        levels.push({
          PalletCount: 0,
          Items: []
        })
      }
    }

    let totalQuantity = 0
    const binDetails = !binsDetails.has(detailedBinId) ? [] : binsDetails.get(detailedBinId);
    binDetails.forEach(pallet => {
      let item = levels[detailedBin.LevelNum - pallet.Level - 1].Items.find(item => item.Id === pallet.ItemId)
      if(!item){
        item = {
          Id: pallet.ItemId,
          Name: this.configStore.items[pallet.ItemId],
          Quantity: 0
        }
        levels[detailedBin.LevelNum - pallet.Level - 1].Items.push(item)
      }
      item.Quantity += pallet.BagCount * pallet.Quantity
      totalQuantity += pallet.BagCount * pallet.Quantity
      levels[detailedBin.LevelNum - pallet.Level - 1].PalletCount++
    })

    return Object.assign({}, detailedBin, {
      Levels: levels,
      TotalQuantity: totalQuantity
    })
  }

  @computed get currentWarehouseBins() {
    const bins = {};
    if(!this.currentWarehouse) return bins;
    for(const corridor of this.currentWarehouse.Corridors) {
      if(!corridor.hasOwnProperty('Blocks')) continue;
      for(const block of corridor.Blocks) {
        if(!block.hasOwnProperty('Bins')) continue;
        for(const bin of block.Bins) {
          bins[bin.Id] = bin;
        }
      }
    }
    return bins;
  }

  @computed get currentWarehouseFilteredBins() {
    if (!this.search) {
      return this.items;
    }
    return observable(this.items.filter(item => {
      const searches = this.search.split(' ')
      for (let i = 0; i < searches.length; i++) {
        const s = searches[i].toLowerCase()
        //console.log(s, item.FilterField)
        if (item.FilterField.indexOf(s) === -1) {
          return false
        }
      }
      return true

      for (let fieldValue of Object.values(item)) {
        if (('' + fieldValue).indexOf(this.search) > -1) {
          return true;
        }
      }
      return false;
    }));
  }

  @computed get detailedBinQuantities() {
    return !this.detailedBinRawContents ? null : this.detailedBinRawContents.reduce((acc, currentContent) => {
      if(!acc.hasOwnProperty(currentContent.ItemId)) {
        acc[currentContent.ItemId] = 0;
      }
      acc[currentContent.ItemId] += (parseInt(currentContent.Quantity) * currentContent.BagCount)
      return acc;
    }, {});
  }

  @computed get detailedBinRawContents() {
    const { binsDetails, detailedBinId } = this;
    return !binsDetails.has(detailedBinId) ? null : binsDetails.get(detailedBinId);
  }

  @computed get currentWarehouse() {
    if(this.currentWarehouseId === null || !this.warehouses) return null;
    return this.warehouses.find(wh => wh.Id === this.currentWarehouseId);
  }

  @computed get scale() {
    const {currentWarehouse, containerRect} = this;
    if(!currentWarehouse || !containerRect) return 1;
    const widthRatio = (containerRect.width - 62) / currentWarehouse.Dimensions.Width;
    const heightRatio = (containerRect.height - 60) / currentWarehouse.Dimensions.Height;
    const scale = widthRatio > heightRatio ? heightRatio : widthRatio
    return Math.min(0.208, scale);
  }

  @observable containerRect = null;
  @action setContainerRect(v) {
    this.containerRect = v;
  }

  @observable mousePosition = { x: 0, y: 0 };
  @action setMousePosition(v) {
    this.mousePosition = v;
  }

  scaled = (valueToScale) => valueToScale * this.scale;
}

export { MapStore, STORE_MAP };